import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import React, { useMemo } from 'react'
import { useBoolean } from 'react-use'

import agent from 'agent'
import Icon from 'components/Icon'
import useAuth from 'hooks/use-auth'
import { useTranslation } from 'hooks/use-translation'
import toast from 'lib/toast'
import {
  ServiceStatusCheck,
  StatusCheck
} from 'types/api/status-checks/status-check'
import { messageFromResponseError } from 'utils/strings'

/**
 * Type guard function to check if the status check is a StatusCheck
 *
 * Currently, the StatusCheckList component renders items of type StatusCheck to generate the list of status checks. For
 * some reason, all of the utility functions and contexts used in the forms are using ServiceStatusCheck. This component
 * is the only component that is used in both contexts. This type guard function determines if the status check that is
 * passed in to this component is a StatusCheck or a ServiceStatusCheck in order to determine the identifier of the status
 * check.
 *
 * TODO: Refactor the status checks form utilities and contexts to use StatusCheck instead of ServiceStatusCheck
 */
function isStatusCheck(statusCheck: any): statusCheck is StatusCheck {
  return Boolean(statusCheck?.identifier)
}

const DeleteStatusCheck: React.FC<{
  statusCheck: StatusCheck | ServiceStatusCheck
  onChanged: () => void
}> = ({ statusCheck, onChanged }) => {
  const { currentTeamId } = useAuth()
  const { trans } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useBoolean(false)

  const statusCheckId = useMemo(() => {
    if (isStatusCheck(statusCheck)) {
      return statusCheck.identifier
    }

    return statusCheck.id
  }, [statusCheck])

  const handleDelete = async () => {
    try {
      await agent.delete(
        `/status-checks/${statusCheckId}?teamId=${currentTeamId}`
      )
      onChanged()
    } catch (err) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          trans('status_checks.list.delete.error')
        )
      })
    } finally {
      setIsModalOpen(false)
    }
  }

  return (
    <Box>
      <Button
        color="error"
        data-test-delete-status-check={statusCheckId}
        data-track-action="delete-status-checks-open-dialog"
        data-track-category="status-checks" // TODO track anything different if it's PNI?
        data-track-on="click"
        size="small"
        startIcon={<Icon icon="trash" />}
        variant="contained"
        onClick={() => setIsModalOpen(true)}
      >
        Delete
      </Button>
      <Dialog
        aria-labelledby="alert-dialog-title"
        data-test-status-check-delete-modal={statusCheckId}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle color="error" id="alert-dialog-title">
          {trans('status_checks.list.delete_modal.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {trans('status_checks.list.delete_modal.text')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            data-test-cancel-button
            data-track-action="delete-status-check-dialog-cancel"
            data-track-category="status-checks" // TODO track anything different if it's PNI?
            data-track-on="click"
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            autoFocus
            data-test-delete-button
            data-track-action="delete-status-check-dialog-delete"
            data-track-category="status-checks" // TODO track anything different if it's PNI?
            data-track-on="click"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DeleteStatusCheck
